import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from '@mypoz/i18n';
import { cn } from '@mypoz/ui';

import { ServiceImage } from '@/components/ServiceImage.js';
import { useFormat } from '@/lib/format.js';

export interface ServiceCardProps {
  imageUrl?: string;
  name: string;
  /** The other language's name, shown beside `name` when it differs. */
  nameNative?: string;
  description: ReactNode;
  /** The vendor's `fee` in cents (MYP-80). Unset → `priceFallback`. */
  feeCents?: number;
  currency?: string;
  /** What to say when there is no price: each screen has its own wording. */
  priceFallback: string;
  checked: boolean;
  onToggle: () => void;
  /** Extra line under the description, e.g. "the warehouse confirms the final charge". */
  note?: ReactNode;
}

/**
 * One optional service, as a card you tick (MYP-116) — the layout of the old site's 额外服务
 * list the mentor pointed to: picture on the left, name and notice in the middle, the checkbox top
 * right and "Service price: RM 8.00" under it. Shared by order Step 3 and the package Add/Edit
 * sheet so the two lists look and behave the same.
 *
 * Tapping anywhere on the card ticks the box: an empty `<label>` is stretched over the whole card.
 * It is an overlay rather than a wrapper because a `<label>` may not contain the "Show more"
 * button, which sits above the overlay so it never ticks the box. The checkbox takes its name
 * from the service name and its description from the notice. The overlay also means the notice's
 * text can't be selected; nothing on the card needs copying.
 *
 * The price moves under the notice whenever the card itself is narrow (a container query, not the
 * screen width): the package sheet is narrow even on a desktop, and a price column there left the
 * notice only a few words wide.
 *
 * MYP-117: some notices run to a dozen lines and pushed Save far down the sheet. They are cut to
 * two lines, with "Show more" only when the text really is cut (measured, so it follows the
 * card's width and the language).
 */
export function ServiceCard({
  imageUrl,
  name,
  nameNative,
  description,
  feeCents,
  currency,
  priceFallback,
  checked,
  onToggle,
  note,
}: ServiceCardProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const inputId = useId();
  const nameId = useId();
  const descriptionId = useId();

  const descriptionRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [truncated, setTruncated] = useState(false);

  // Measured only while clamped: once expanded the text fits by definition, and the button must
  // stay to fold it back.
  useLayoutEffect(() => {
    const element = descriptionRef.current;
    if (!element || expanded) return;
    const measure = () => setTruncated(element.scrollHeight > element.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [expanded, description]);

  const price =
    feeCents === undefined ? (
      <span className="text-sm text-muted">{priceFallback}</span>
    ) : (
      <span className="text-sm whitespace-nowrap text-ink">
        {t('services.priceLabel')}{' '}
        <span className="font-semibold text-brand tabular-nums">
          {fmt.money(feeCents, currency)}
        </span>
      </span>
    );

  return (
    <div
      className={cn(
        '@container relative flex gap-3 rounded-lg border p-4 transition-colors',
        checked ? 'border-brand' : 'border-line hover:border-muted-soft',
      )}
    >
      <label htmlFor={inputId} aria-hidden="true" className="absolute inset-0 cursor-pointer" />

      <ServiceImage src={imageUrl} />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span id={nameId} className="text-sm font-semibold text-ink">
            {name}
          </span>
          {nameNative ? (
            <span lang="zh" className="text-sm text-muted">
              {nameNative}
            </span>
          ) : null}
        </div>
        <div
          ref={descriptionRef}
          id={descriptionId}
          className={cn('mt-0.5 text-sm text-muted', !expanded && 'line-clamp-2')}
        >
          {description}
        </div>
        {truncated ? (
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={descriptionId}
            onClick={() => setExpanded((current) => !current)}
            className="relative z-10 inline-flex min-h-11 items-center text-sm font-medium text-brand hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand lg:min-h-0 lg:mt-1"
          >
            {expanded ? t('services.showLess') : t('services.showMore')}
          </button>
        ) : null}
        {note ? <div className="mt-1 text-sm text-muted">{note}</div> : null}
        <div className="mt-2 @md:hidden">{price}</div>
      </div>

      <div className="flex shrink-0 flex-col items-end justify-between gap-3">
        <input
          id={inputId}
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          aria-labelledby={nameId}
          aria-describedby={descriptionId}
          className="size-5 shrink-0 accent-brand"
        />
        <div className="hidden @md:block">{price}</div>
      </div>
    </div>
  );
}
